/**
 * Subscription funnel analytics - rastreia eventos do checkout de assinatura Stripe.
 * Foco em erros para debug.
 */

import { Platform } from 'react-native';
import * as Sentry from '@sentry/react-native';
import api from '@/services/api';
import { getAttributionFromCookie } from '@/lib/attribution';

export type SubscriptionAnalyticsEvent =
  | 'subscription_checkout_start'
  | 'subscription_checkout_success'
  | 'subscription_checkout_error'
  | 'subscription_create_session_error'
  | 'subscription_confirm_error';

export interface SubscriptionAnalyticsPayload {
  event: SubscriptionAnalyticsEvent;
  priceId?: string;
  errorMessage?: string;
  httpStatus?: number;
  sessionId?: string;
  utmSource?: string;
  utmCampaign?: string;
}

const SESSION_ID_KEY = 'subscription_analytics_session';

function getOrCreateSessionId(): string {
  if (typeof window === 'undefined') return '';
  let sid = sessionStorage.getItem(SESSION_ID_KEY);
  if (!sid) {
    sid = `sub_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
    sessionStorage.setItem(SESSION_ID_KEY, sid);
  }
  return sid;
}

function getAttributionContext(): Partial<SubscriptionAnalyticsPayload> {
  if (typeof window === 'undefined') return {};
  const att = getAttributionFromCookie();
  if (!att) return {};
  return {
    utmSource: att.utmSource ?? undefined,
    utmCampaign: att.utmCampaign ?? undefined,
  };
}

export function trackSubscriptionEvent(payload: SubscriptionAnalyticsPayload): void {
  Sentry.addBreadcrumb({
    category: 'subscription',
    message: payload.event,
    level: payload.event.endsWith('_error') ? 'error' : 'info',
    data: { priceId: payload.priceId, httpStatus: payload.httpStatus },
  });

  if (__DEV__) return;
  if (Platform.OS !== 'web') return;

  const sessionId = getOrCreateSessionId();
  const attribution = getAttributionContext();

  const body = {
    event: payload.event,
    errorField: payload.priceId ? `priceId=${payload.priceId}` : undefined,
    errorMessage: payload.errorMessage,
    httpStatus: payload.httpStatus,
    sessionId,
    ...attribution,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
  };

  api.post('/analytics/register-events', body).catch(() => {});
}
